<!--
  - Copyright (c) 2022-2024. Haodong JU
  -->

<template>
  <div>
    <ik-data-table
      :icon="'mdi-devices'"
      :model="model"
      :required-date-value="$parent.dateRange"
      :use-default-action="true"
      :use-delete-action="false"
      :use-edit-action="false"
      entity-name="cashBook"
      @reloaded="reload"
    >
      <template #topOnTable="{tableItems}">
        <div
          :class="$vuetify.breakpoint.mdAndUp ? 'd-flex align-center' : ''"
          class="mb-4"
          style="width: 100%;"
        >
          <v-card
            class="pa-4 d-flex flex-column"
            elevation="0"
            outlined
            style="background: white;border-radius: 24px !important;white-space: nowrap"
            width="100%"
          >
            <div class="d-flex align-center">
              <v-icon>
                mdi-arrow-up-bold-box-outline
              </v-icon>
              <div class="text-subtitle-2">
                {{ $t('totalIncome') }}
              </div>
            </div>
            <div class="text-caption">
              {{ $t('nowTimeRange') }}
            </div>
            <div class="text-h3 font-weight-black mt-4 success--text">
              {{ getTotal(tableItems.filter(it => it.payLogAmount > 0)) | priceDisplay }}
            </div>
          </v-card>
          <v-card
            :class="$vuetify.breakpoint.lgAndUp ? 'ml-2' : 'mt-2'"
            class="pa-4 d-flex flex-column"
            elevation="0"
            outlined
            style="background: white;border-radius: 24px !important;white-space: nowrap"
            width="100%"
          >
            <div class="d-flex align-center">
              <v-icon>
                mdi-arrow-down-bold-box-outline
              </v-icon>
              <div class="text-subtitle-2">
                {{ $t('totalExpend') }}
              </div>
            </div>
            <div class="text-caption">
              {{ $t('nowTimeRange') }}
            </div>
            <div
              class="text-h3 font-weight-black mt-4 error--text"
            >
              {{ -1 * getTotal(tableItems.filter(it => lessThanZero(it.payLogAmount))) | priceDisplay }}
            </div>
          </v-card>
          <v-card
            :class="$vuetify.breakpoint.lgAndUp ? 'ml-2' : 'mt-2'"
            class="pa-4 d-flex flex-column"
            elevation="0"
            outlined
            style="background: white;border-radius: 24px !important;white-space: nowrap"
            width="100%"
          >
            <div class="d-flex align-center">
              <v-icon>mdi-bank</v-icon>
              <div class="text-subtitle-2">
                {{ $t('totalCash') }}
              </div>
            </div>
            <div class="text-caption">
              {{ $t('nowTimeRange') }}
            </div>
            <div class="text-h3 font-weight-black mt-4">
              {{ currentAmount| priceDisplay }}
            </div>
          </v-card>
        </div>
      </template>
      <template #right>
        <primary-button
          text="清点现金"
          @click="openCashDialog"
        />
      </template>
      <template #footer>
        <!-- Display information about cash book here if needed -->
      </template>
    </ik-data-table>
    <v-dialog
      v-model="showCashCountDialog"
      max-width="600"
      persistent
    >
      <v-card
        v-if="showCashCountDialog"
        class="px-4 pb-2"
      >
        <page-sub-header>
          {{ $t('cashCalculator') }}
          <template #action>
            <template v-if="currentAmount!==total">
              <small-base-card>{{ $t('totalValue') }} {{ currentAmount | priceDisplay }}</small-base-card>
              <small-base-card>{{ $t('difference') }} {{ currentAmount - total | priceDisplay }}</small-base-card>
            </template>
          </template>
        </page-sub-header>
        <div class="text-body-1 mb-2">
          {{ $t('cashTotal') }}{{ cashTotal | priceDisplay }}
        </div>
        <div style="display: grid;grid-template-columns: repeat(3,1fr);grid-gap: 4px;">
          <v-card
            v-for="i in bigCashType"
            :key="i"
            class="pa-4"
            color="grey lighten-5"
            flat
          >
            <div class="text-h4 d-flex align-center">
              {{ i |priceDisplay }}
              <v-spacer />&times;
              {{ cashCount[i] }}
            </div>
            <v-btn
              color="red lighten-3"
              elevation="0"
              x-small
              @click="addCashAmount(i,-1)"
            >
              <div class="text-body-1">
                -1
              </div>
            </v-btn>
            <v-btn
              color="green lighten-4"
              elevation="0"
              x-small
              @click="addCashAmount(i,1)"
            >
              <div class="text-body-1">
                +1
              </div>
            </v-btn>
            <v-btn
              color="green lighten-3"
              elevation="0"
              x-small
              @click="addCashAmount(i,10)"
            >
              <div class="text-body-1">
                +10
              </div>
            </v-btn>
            <v-btn
              color="green lighten-2"
              elevation="0"
              x-small
              @click="addCashAmount(i)"
            >
              <div class="text-body-1">
                +
              </div>
            </v-btn>
          </v-card>
        </div>
        <div class="text-body-1 mb-2 mt-8">
          {{ $t('coinTotal') }}{{ coinTotal | priceDisplay }}
        </div>
        <div style="display: grid;grid-template-columns: repeat(3,1fr);grid-gap: 4px;">
          <v-card
            v-for="i in coinCashType"
            :key="i"
            class="pa-4"
            color="grey lighten-5"
            flat
          >
            <div class="text-h4 d-flex align-center">
              {{ i |priceDisplay }}
              <v-spacer />&times;
              {{ cashCount[i] }}
            </div>
            <v-btn
              color="red lighten-3"
              elevation="0"
              x-small
              @click="addCashAmount(i,-1)"
            >
              <div class="text-body-1">
                -1
              </div>
            </v-btn>
            <v-btn
              color="green lighten-4"
              elevation="0"
              x-small
              @click="addCashAmount(i,1)"
            >
              <div class="text-body-1">
                +1
              </div>
            </v-btn>
            <v-btn
              color="green lighten-3"
              elevation="0"
              x-small
              @click="addCashAmount(i,10)"
            >
              <div class="text-body-1">
                +10
              </div>
            </v-btn>
            <v-btn
              color="green lighten-2"
              elevation="0"
              x-small
              @click="addCashAmount(i)"
            >
              <div class="text-body-1">
                +
              </div>
            </v-btn>
          </v-card>
        </div>
        <v-btn
          block
          class="mt-2"
          color="green darken-4 white--text"
          elevation="0"
          x-large
          @click="showCashCountDialog=false"
        >
          <v-icon left>
            mdi-check
          </v-icon>
          {{ $t('altogether') }} {{ total | priceDisplay }}
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import CashBook from '@/model/dataLayer/api/bills/cashbook/CashBook'
import { getCurrentCashAmount } from '@/model/dataLayer/service/otherService'
import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import PrimaryButton from '@/views/AadenPages/components/PrimaryButton.vue'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'
import SmallBaseCard from '@/views/widget/uiComponent/SmallBaseCard.vue'
import IKUtils from 'innerken-js-utils'
import { values } from 'lodash-es'
import { sumBy } from 'lodash'

export default {
  name: 'CashBookPage',
  components: {
    SmallBaseCard,
    PageSubHeader,
    PrimaryButton,
    IkDataTable
  },
  data () {
    return {
      model: CashBook,
      currentAmount: -1,
      bigCashType: [200, 100, 50, 20, 10, 5],
      coinCashType: [2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01],
      cashCount: null,
      loading: false,
      showCashCountDialog: false
    }
  },
  computed: {
    coinTotal () {
      return sumBy(this.coinCashType.map(it => it * this.cashCount[it]))
    },
    cashTotal () {
      return sumBy(this.bigCashType.map(it => it * this.cashCount[it]))
    },
    total () {
      return this.coinTotal + this.cashTotal
    }
  },
  watch: {
    '$parent.dateRange': {
      handler: function (val) {
        this.reload()
      },
      deep: true,
      immediate: true
    }
  },
  async mounted () {
    await this.initialize()
  },
  methods: {
    async addCashAmount (amount, count = null) {
      if (!count) {
        this.showCashCountDialog = false
        count = parseInt(await IKUtils.showInput('请输入张数', 'number', ''))
        this.showCashCountDialog = true
      }
      if (this.cashCount[amount] + count >= 0) {
        this.$set(this.cashCount, amount, this.cashCount[amount] + count)
        console.log(this.cashCount)
      }
    },
    openCashDialog () {
      const cashArr = [...this.bigCashType, ...this.coinCashType]
      this.cashCount = Object.fromEntries(values(cashArr).map(it => [it, 0]))
      for (const amount of cashArr) {
        this.cashCount[amount] = 0
      }
      this.showCashCountDialog = true
    },
    lessThanZero (item) {
      return item < 0
    },
    getTotal (items) {
      return items.reduce((sum, i) => sum + i.payLogAmount, 0)
    },
    async reload () {
      this.loading = true
      this.currentAmount = await getCurrentCashAmount()
      this.loading = false
    },
    async initialize () {
      await this.reload()
    }

  }
}

</script>
<style>
.hiddenText {
  display: inline-block;
  word-break: keep-all;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hiddenText:hover {
  overflow: visible;
}
</style>
